import React from 'react'
import withRedux from 'next-redux-wrapper'
import parse from 'lib/url/parse'
import Theme from 'components/Theme'
import { App, Store } from 'apps/public/feature'
import { Actions as PageActions } from 'actions/page'
import { Actions as AlgoliaActions } from 'actions/algolia2/data'
import { getDataLayer, getMetaData } from 'apps/public/feature/selectors/app'
import {
  getAlgoliaUserToken,
  getSlugifyDictionary,
  getAlgoliaSearchIndice,
} from 'selectors/algolia2/data'
import {
  selectedFacetsToAlgoliaQueryString,
  convertSlugifiedFacetsToNaturalValues,
} from 'lib/algolia/v2'
import { algoliaFeatureApi } from 'apis/algolia'
import { storeSetup } from 'lib/pageSetup'
import { getUserPreferencesDestinationZone } from 'selectors/user/cookie'
import { setAlgoliaQueryIDInLocalStorage } from 'lib/algolia/v2/helpers'

const HITS_PER_PAGE = 50

const setupFeatureData = async (
  query: Record<string, any>,
  destinationZone: string
): Promise<Record<string, any>> => {
  try {
    const { algoliaSetup, category, has_prints, keyword, hitsPerPage, page } = query
    // Configuration Data
    const { facets, sharedFacetNames, slugifyDictionary } = algoliaSetup
    const selectedFacets = convertSlugifiedFacetsToNaturalValues({
      dictionary: slugifyDictionary,
      slugifiedFacets: {
        category,
        has_prints,
      },
    })
    // We only want AVAIL => FOR SALE artworks - Manually add query
    const overrideFacets = {
      original_status: 'avail',
      ...selectedFacets,
    }
    const filters = selectedFacetsToAlgoliaQueryString(overrideFacets)
    const algoliaResults = await algoliaFeatureApi({
      filters,
      hitsPerPage: hitsPerPage || HITS_PER_PAGE,
      page,
      query: keyword,
      destinationZone,
    })
    // Expect to Match ALGOLIA Reducer - Initial Setup
    return {
      data: algoliaResults,
      facets: facets || {},
      hitsPerPage: hitsPerPage || HITS_PER_PAGE,
      selectedFacets: selectedFacets || {},
      sharedFacetNames,
      slugifyDictionary,
    }
  } catch (error) {
    console.error('setupFeatureData', {
      error,
    })
    return {}
  }
}

function Feature() {
  return (
    <Theme>
      <App />
    </Theme>
  )
}

Feature.clientSideTransitions = async ({ store, query }) => {
  if (window) {
    store.dispatch(PageActions.IS_READY(false))
    const { href } = window.location

    const { category, has_prints, keyword, page, hitsPerPage } = query
    // Redux Data
    const state = { ...store.getState() }
    const slugifyDictionary = getSlugifyDictionary(state)
    const userToken = getAlgoliaUserToken(state)
    const searchIndice = getAlgoliaSearchIndice(state)
    const selectedFacets = convertSlugifiedFacetsToNaturalValues({
      dictionary: slugifyDictionary,
      slugifiedFacets: {
        category,
        has_prints,
      },
    })
    const destinationZone = getUserPreferencesDestinationZone(state)
    // We only want AVAIL => FOR SALE artworks - Manually add query
    const overrideFacets = {
      original_status: 'avail',
      ...selectedFacets,
    }
    const filters = selectedFacetsToAlgoliaQueryString(overrideFacets)
    // We have the default Indice but we need to apply SORT logic, if it exist
    const algoliaResults = await algoliaFeatureApi({
      filters,
      hitsPerPage: hitsPerPage || HITS_PER_PAGE,
      page,
      query: keyword,
      searchIndice,
      userToken,
      destinationZone,
    })
    // store ALGOLIA_QUERY_ID in local storage
    const { queryID } = algoliaResults
    setAlgoliaQueryIDInLocalStorage(queryID)
    // Dispatch Array<ACTIONS>
    store.dispatch([
      AlgoliaActions.UPDATE({
        data: algoliaResults,
      }),
      PageActions.SET({
        path: parse(href).path || '',
        params: {
          category,
          keyword,
          page,
          hitsPerPage,
        },
      }),
      PageActions.IS_READY(true),
    ])
    // Data Layer
    const dataLayer = getDataLayer({ ...store.getState() })
    window.dataLayer.push({ ...dataLayer, event: 'pageview' })
  }
}

Feature.getInitialProps = async ({ req, res, store, query }) => {
  // Client-Side
  if (!req) {
    Feature.clientSideTransitions({
      store,
      query,
    })
    const state = { ...store.getState() }
    const metaData = getMetaData(state)
    return {
      metaData,
    }
  }

  // SSR
  storeSetup(store, req)
  const { originalUrl, params, query: reqQuery, destinationZone } = req
  const pageData = {
    data: {},
    name: 'public/feature',
    params: { ...params, ...reqQuery },
    path: originalUrl,
  }
  // Lets FETCH Algolia data
  const algoliaData = await setupFeatureData(query, destinationZone)

  // if total results from algolia is less than 10 redirect to main browse category page
  // Ex: /collage/aaa/feature -> /collage
  if (algoliaData?.data?.hits?.total < 10) {
    // This BREAKS clientside - if you import at TOP
    const { saRedirect } = require('server/helpers')

    return saRedirect(req, res, `/${req.params.category}`, 302)
  }

  store.dispatch(AlgoliaActions.INITIAL_SETUP(algoliaData))
  // Page Data Setup
  store.dispatch(PageActions.SET(pageData))
  // Akamai Cache Tag
  res.set('Edge-Cache-Tag', `page-feature`)
  // Metadata & Datalayer
  const state = { ...store.getState() }
  const metaData = getMetaData(state)
  const dataLayer = getDataLayer(state)
  return {
    dataLayer,
    metaData,
  }
}

export default withRedux(Store)(Feature)
